import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { heritageColors } from '../../../../heritageTheme.ts';
import { getIconById } from '../../../NitzavSpotlightWidget/iconOptions';
import { PARNAS_TYPE_LABELS, fetchHebDateLabel } from '../../parnasConstants';

/**
 * WeekAhead — נציב יום מפואר + 7 ימים קדימה
 * מלבן גדול ומלכותי לנציב היום, ומתחתיו 7 מלבנים עם
 * שם היום, תאריך עברי ושם הנציב (או עידוד לתרום).
 */

const HEBREW_DAYS = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

function getNextDays(effectiveDateISO, count = 7) {
  const base = effectiveDateISO ? new Date(effectiveDateISO + 'T12:00:00') : new Date();
  const days = [];
  for (let i = 1; i <= count; i++) {
    const d = new Date(base);
    d.setDate(d.getDate() + i);
    days.push({
      iso:       `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`,
      hebrewDay: HEBREW_DAYS[d.getDay()],
    });
  }
  return days;
}

const GOLD = '#b8860b';

// ─── Component ────────────────────────────────────────────────────────────────

const WeekAhead = ({
  activeSponsors        = [],
  parnasType            = 'maor',
  parnasTypeCustomLabel = '',
  parnasTypeIcon        = 'star',
  currentParshaName     = '',
  todayHebrewLabel      = '',
  title                 = '',
  showTitle             = false,
  sponsors              = [],
  effectiveDateISO      = '',
  placeholderName       = 'השם שלך כאן',
}) => {
  const TypeIcon  = getIconById(parnasTypeIcon || 'star');
  const typeLabel = parnasType === 'acher' && parnasTypeCustomLabel
    ? parnasTypeCustomLabel
    : (PARNAS_TYPE_LABELS[parnasType] || '');

  const todayDaily = activeSponsors.find(s => s.slotType === 'daily') || null;
  const nextDays   = getNextDays(effectiveDateISO, 7);

  const dailySponsorByDate = useMemo(() => {
    const map = {};
    for (const sponsor of sponsors)
      for (const slot of (sponsor.slots || []))
        if (slot.type === 'daily' && slot.date)
          map[slot.date] = { sponsorName: sponsor.name, note: slot.note || '' };
    return map;
  }, [sponsors]);

  // ── Hebrew dates for the 7 upcoming days ──────────────────────────────────
  const [hebDates, setHebDates] = useState({});   // { iso: "כ"ז אדר" }

  useEffect(() => {
    if (!nextDays.length) return;
    let cancelled = false;

    Promise.all(
      nextDays.map(({ iso }) =>
        fetchHebDateLabel(iso).then(label => ({ iso, label }))
      )
    ).then(results => {
      if (cancelled) return;
      const map = {};
      results.forEach(({ iso, label }) => { map[iso] = label; });
      setHebDates(map);
    });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveDateISO]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Box dir="rtl" sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <Box sx={{
        px: 2, pt: 1.5, pb: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.75,
      }}>
        <TypeIcon sx={{ fontSize: 18, color: heritageColors.gold }} />
        <Typography variant="h6" sx={{ color: heritageColors.navy, fontWeight: 700 }}>
          {showTitle && title ? title : `פרנסי ${typeLabel}`}
        </Typography>
      </Box>

      <Divider sx={{ borderColor: heritageColors.border }} />

      {/* Today — hero card: flex: 2 כדי לגדול יחד עם הווידג'ט */}
      <Box sx={{ px: 1.5, pt: 1.25, pb: 1, flex: 2, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          border: `2px solid ${GOLD}`,
          borderRadius: '8px',
          px: 3,
          py: 2,
          textAlign: 'center',
          gap: 1,
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(160deg, #fffdf7 0%, #fef8e4 55%, #fdf1cc 100%)',
          boxShadow: '0 4px 20px rgba(184,134,11,0.20), 0 1px 4px rgba(0,0,0,0.06)',
        }}>
          {/* Top gold accent stripe */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: '3px',
            background: `linear-gradient(to left, transparent, ${GOLD} 25%, #d4a017 50%, ${GOLD} 75%, transparent)`,
          }} />

          <Typography variant="caption" sx={{
            color: GOLD, fontWeight: 700, fontSize: '0.68em', letterSpacing: '0.08em',
          }}>
            {todayHebrewLabel ? `נציב היום  ·  ${todayHebrewLabel}` : 'נציב היום'}
          </Typography>

          <Box sx={{ width: '40%', height: '1px', bgcolor: GOLD, opacity: 0.3, my: 0.25 }} />

          <Typography variant="h4" sx={{
            color:      todayDaily ? heritageColors.navy : heritageColors.slate,
            fontWeight: 800,
            lineHeight: 1.2,
            opacity:    todayDaily ? 1 : 0.3,
            wordBreak:  'break-word',
          }}>
            {todayDaily ? todayDaily.sponsorName : placeholderName}
          </Typography>

          {!todayDaily && (
            <Typography variant="body2" sx={{
              color: heritageColors.slate, fontSize: '0.72em',
              mt: 0.25, opacity: 0.55, lineHeight: 1.3,
            }}>
              ניתן לתרום ליום זה — פנה לגבאי בית הכנסת
            </Typography>
          )}

          {todayDaily?.note && (
            <Typography variant="body2" sx={{
              color: heritageColors.slate, fontStyle: 'italic',
              fontSize: '0.75em', mt: 0.25, lineHeight: 1.3,
            }}>
              {todayDaily.note}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Section label */}
      <Box sx={{
        px: 2, pt: 0.25, pb: 0.5,
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        <Typography variant="caption" sx={{
          color: heritageColors.slate, fontSize: '0.65em', fontWeight: 600,
          letterSpacing: '0.05em', whiteSpace: 'nowrap',
        }}>
          7 ימים קדימה
        </Typography>
        <Box sx={{ flex: 1, height: '1px', bgcolor: heritageColors.border }} />
      </Box>

      {/* 7-day tiles: flex: 3 ביחס ל-flex: 2 של הכרטיס המרכזי */}
      <Box sx={{
        flex: 3,
        minHeight: 0,
        display: 'flex',
        gap: 0.5,
        px: 1.25,
        pb: 1.25,
        overflow: 'hidden',
      }}>
        {nextDays.map(({ iso, hebrewDay }) => {
          const sponsor  = dailySponsorByDate[iso] || null;
          const hebLabel = hebDates[iso] || '…';

          return (
            <Box
              key={iso}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                border: `1.5px solid ${sponsor ? GOLD : heritageColors.border}`,
                borderRadius: '6px',
                px: 0.5,
                py: 1.25,
                textAlign: 'center',
                gap: 0.3,
                background: sponsor
                  ? 'linear-gradient(to bottom, #fffdf7 0%, #fdf8e8 100%)'
                  : 'linear-gradient(to bottom, #fafaf9 0%, #f5f5f3 100%)',
                boxShadow: sponsor
                  ? '0 2px 10px rgba(184,134,11,0.14)'
                  : '0 1px 3px rgba(0,0,0,0.04)',
                overflow: 'hidden',
              }}
            >
              {/* Day name */}
              <Typography sx={{
                color: heritageColors.navy, fontSize: '0.62em',
                fontWeight: 700, lineHeight: 1.1,
              }}>
                {hebrewDay}
              </Typography>

              {/* Hebrew date */}
              <Typography sx={{
                color: heritageColors.slate, fontSize: '0.56em',
                opacity: 0.75, lineHeight: 1.1, whiteSpace: 'nowrap',
              }}>
                {hebLabel}
              </Typography>

              {/* Divider */}
              <Box sx={{
                width: '60%', height: '1px',
                bgcolor: sponsor ? GOLD : heritageColors.border,
                opacity: 0.4, my: 0.3,
              }} />

              {sponsor ? (
                /* Sponsor name */
                <Typography sx={{
                  color: heritageColors.navy, fontWeight: 700,
                  fontSize: '0.64em', lineHeight: 1.2, wordBreak: 'break-word',
                }}>
                  {sponsor.sponsorName}
                </Typography>
              ) : (
                /* Encourage donation */
                <>
                  <Typography sx={{
                    color: heritageColors.gold, fontWeight: 700,
                    fontSize: '0.6em', lineHeight: 1.2,
                  }}>
                    פנוי
                  </Typography>
                  <Typography sx={{
                    color: heritageColors.slate, fontSize: '0.52em',
                    opacity: 0.65, lineHeight: 1.2, mt: 0.1,
                  }}>
                    לזכות לתרום
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      {(currentParshaName || todayHebrewLabel) && (
        <>
          <Divider sx={{ borderColor: heritageColors.border }} />
          <Box sx={{
            px: 2, py: 0.6,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            {currentParshaName ? (
              <Typography variant="body2" sx={{ color: heritageColors.slate, fontSize: '0.8em' }}>
                פרשת {currentParshaName}
              </Typography>
            ) : <Box />}
            {todayHebrewLabel && (
              <Typography variant="body2" sx={{ color: heritageColors.slate, fontSize: '0.8em' }}>
                {todayHebrewLabel}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default WeekAhead;
